import React from "react";
import "./DownloadOptions.css";
import DownloadFormatModal from "./DownloadFormatModal";
import { useImageContext } from "../../contexts/ImageContext";
import { IS_APP_V1 } from "../../config/app/appVersion";

const stripFileExtension = (name) => {
  if (!name || typeof name !== "string") return "image";
  const base = name.trim();
  const dotIndex = base.lastIndexOf(".");
  if (dotIndex <= 0) return base;
  return base.slice(0, dotIndex);
};

const DownloadOptions = ({
  onClose = () => {},
  onDownload = () => {},
  downloading = false,
  errorMessage = null,
}) => {
  const { originalFileName } = useImageContext();
  const [fileName, setFileName] = React.useState(() =>
    stripFileExtension(originalFileName)
  );

  React.useEffect(() => {
    if (originalFileName) {
      setFileName(stripFileExtension(originalFileName));
    }
  }, [originalFileName]);
  const [format, setFormat] = React.useState("jpeg");
  const [exportSize, setExportSize] = React.useState("standard");
  const [showFormatModal, setShowFormatModal] = React.useState(false);
  const [modalPosition, setModalPosition] = React.useState({ top: 0, left: 0 });
  const formatButtonRef = React.useRef(null);

  const formatOptions = [
    {
      id: "jpeg",
      name: "JPEG",
      label: "JPEG (Recommended)",
      description: "Smaller file size, transparency is not saved",
    },
    {
      id: "png",
      name: "PNG",
      label: "PNG",
      description: "Bigger file size, transparency is saved",
    },
    {
      id: "webp",
      name: "WEBP",
      label: "WEBP",
      description: "Smallest file size, transparency is saved",
    },
  ];

  const handleDownload = () => {
    if (downloading) return;
    onDownload({
      fileName,
      format,
      exportSize,
    });
  };

  const getFormatLabel = () => {
    return formatOptions.find((opt) => opt.id === format)?.name || "Dropdown";
  };

  return (
    <div className="download-options-overlay" onClick={onClose}>
      <div
        className="download-options-container"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="download-options-header">
          <h2>Download</h2>
          <button className="close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="download-options-content">
          <div className="form-group">
            <label className="form-label">File Name</label>
            <input
              type="text"
              className="file-name-input"
              value={fileName}
              onChange={(e) => setFileName(e.target.value)}
              placeholder="Enter file name"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Format</label>
            <div className="format-field-wrapper">
              <button
                ref={formatButtonRef}
                className="format-button"
                onClick={() => {
                  if (formatButtonRef.current) {
                    const rect = formatButtonRef.current.getBoundingClientRect();
                    setModalPosition({
                      top: rect.bottom + 8,
                      left: rect.left,
                      width: rect.width,
                    });
                  }
                  setShowFormatModal(true);
                }}
              >
                <span>{getFormatLabel()}</span>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
              {showFormatModal && (
                <DownloadFormatModal
                  isOpen={showFormatModal}
                  selectedFormat={format}
                  onSelectFormat={setFormat}
                  onClose={() => setShowFormatModal(false)}
                  position={modalPosition}
                />
              )}
            </div>
            <div className="format-description">
              {formatOptions.find((opt) => opt.id === format)?.description}
            </div>
          </div>

          <div className="form-group">
            <div className="form-label-with-tooltip">
              <label className="form-label">Export Size</label>
              <div className="tooltip-wrapper">
                <svg className="tooltip-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="16" x2="12" y2="12"></line>
                  <line x1="12" y1="8" x2="12.01" y2="8"></line>
                </svg>
                <div className="tooltip-text">Choose the output resolution for your image, You can export your images for Free upto 2000 x 2000px. </div>
              </div>
            </div>
            <div className="export-size-options">
              <div className="radio-option">
                <input
                  type="radio"
                  id="standard"
                  name="exportSize"
                  value="standard"
                  checked={exportSize === "standard"}
                  onChange={(e) => setExportSize(e.target.value)}
                  className="radio-input"
                />
                <label htmlFor="standard" className="radio-label">
                  <span className="radio-custom"></span>
                  <div className="radio-content">
                    <span className="radio-text">Standard</span>
                    <span className="radio-size-hint">Size: 1500 x 2000px</span>
                  </div>
                </label>
              </div>

              {!IS_APP_V1 && (
                <div className="radio-option">
                  <input
                    type="radio"
                    id="fullResolution"
                    name="exportSize"
                    value="fullResolution"
                    checked={exportSize === "fullResolution"}
                    onChange={(e) => setExportSize(e.target.value)}
                    className="radio-input"
                  />
                  <label htmlFor="fullResolution" className="radio-label">
                    <span className="radio-custom"></span>
                    <div className="radio-content">
                      <span className="radio-text">
                        Full resolution <span className="pro-badge">Pro</span>
                      </span>
                      <span className="radio-size-hint">Size: 1500 x 2000px</span>
                    </div>
                  </label>
                </div>
              )}
            </div>
          </div>
        </div>

        {errorMessage ? (
          <p style={{ color: "#f66", fontSize: 13, padding: "0 16px 8px", margin: 0 }}>
            {errorMessage}
          </p>
        ) : null}

        <div className="download-options-footer">
          <button
            className="download-btn"
            onClick={handleDownload}
            disabled={downloading}
          >
            {downloading ? "Downloading…" : "Download"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DownloadOptions;
